import { useEffect, useState, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Loader2, Users, Building2, DollarSign, ShieldAlert, TrendingUp, Download, Gauge, ArrowLeft, X, ScanSearch, Inbox,
} from "lucide-react";
import api from "@/lib/api";
import { usePresentation } from "@/context/PresentationContext";
import { useUniversalIntake } from "@/context/UniversalIntakeContext";
import { fmtCurrency, exportToCsv } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const NAVY = "#17324D", SLATE = "#315A7D", GOLD = "#B28A3D", EMER = "#0f9d6b", RED = "#c0492f";
const PIE = [EMER, RED, GOLD, SLATE];

export default function AgencyCommandCenter() {
  const { isDemo } = usePresentation();
  const { openIntake } = useUniversalIntake();
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);
  const [vendorDash, setVendorDash] = useState(null);
  const openDrill = (metric, key = "") => setDrill({ metric, key });

  const load = useCallback(() => {
    setD(null);
    api.get(`/command-center/agency?mode=${isDemo ? "demo" : "live"}`)
      .then(({ data }) => setD(data)).catch(() => setD({ error: true }));
    if (!isDemo) api.get("/universal-intake/dash/vendor-intakes").then(({ data }) => setVendorDash(data)).catch(() => setVendorDash(null));
    else setVendorDash(null);
  }, [isDemo]);
  useEffect(load, [load]);

  if (!d) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const lc = d.lead_control || {}, fin = d.financial || {}, pf = d.policy_flow || {}, risk = d.risk || {};

  const policyFlowData = [
    { name: "Issued", value: pf.issued || 0 }, { name: "Declined", value: pf.declined || 0 },
    { name: "Withdrawn", value: pf.withdrawn || 0 }, { name: "Paid", value: pf.paid || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="agency-command-center">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Agency Operations Command Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {d.agency_name} · executive view of lead control, agent accountability, vendor performance, financial operations, and risk.
          </p>
        </div>
        <Button variant="outline" size="sm" data-testid="export-agents"
          onClick={() => exportToCsv("agent-accountability.csv", d.agents || [],
            [{ key: "name", label: "Agent" }, { key: "workload", label: "Workload" }, { key: "issued", label: "Issued" }, { key: "paid", label: "Paid" }, { key: "conversion", label: "Conversion %" }])}>
          <Download className="mr-1.5 h-4 w-4" /> Export
        </Button>
      </div>

      {d.today && (
        <Section title="Today — Operations & Calls" icon={Gauge}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="cc-today">
            <Stat label="New Leads" value={d.today.new_leads} />
            <Stat label="Unassigned" value={d.today.unassigned_leads} warn={d.today.unassigned_leads > 0} />
            <Stat label="Untouched" value={d.today.untouched_leads} warn={d.today.untouched_leads > 0} />
            <Stat label="Overdue Follow-Ups" value={d.today.overdue_followups} warn={d.today.overdue_followups > 0} />
            <Stat label="Calls Attempted" value={d.today.calls_attempted} />
            <Stat label="Calls Answered" value={d.today.calls_answered} />
            <Stat label="Missed / Failed" value={d.today.calls_missed_failed} warn={d.today.calls_missed_failed > 0} />
            <Stat label="Talk Time (min)" value={Math.round((d.today.talk_time_sec || 0) / 60)} />
            <Stat label="Appointments" value={d.today.appointments_scheduled} />
            <Stat label="Apps Started" value={d.today.applications_started} />
            <Stat label="Apps Submitted" value={d.today.applications_submitted} />
            <Stat label="Needs Intervention" value={d.today.needs_intervention} warn={d.today.needs_intervention > 0} />
          </div>
        </Section>
      )}

      {/* New Vendor Intakes + Universal Intake */}
      <Section title="New Vendor Intakes" icon={Inbox}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button size="sm" className="bg-[#B28A3D] text-[#17324D] hover:bg-[#c99b45]" data-testid="cc-universal-intake" onClick={() => openIntake()}>
            <ScanSearch className="mr-1.5 h-4 w-4" /> Universal Intake
          </Button>
          <span className="text-[11px] text-muted-foreground">Vendor submissions flow into Lead Review; internal users can also paste/upload directly.</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="vendor-intakes-panel">
          <Stat label="Pending Review" value={vendorDash?.pending_review ?? 0} warn={(vendorDash?.pending_review ?? 0) > 0} />
          <Stat label="Accepted" value={vendorDash?.accepted ?? 0} />
          <Stat label="Rejected" value={vendorDash?.rejected ?? 0} />
          <Stat label="Needs Info" value={vendorDash?.needs_info ?? 0} warn={(vendorDash?.needs_info ?? 0) > 0} />
          <Stat label="Unassigned" value={vendorDash?.unassigned ?? 0} warn={(vendorDash?.unassigned ?? 0) > 0} />
          <Stat label="Ready for InstaBrain" value={vendorDash?.ready_instabrain ?? 0} />
        </div>
      </Section>

      {/* Financial KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Expected Commission" value={fmtCurrency(fin.expected)} icon={TrendingUp} tone="navy" onClick={() => openDrill("expected")} />
        <Kpi label="Commission Received" value={fmtCurrency(fin.received)} icon={DollarSign} tone="emer" />
        <Kpi label="Awaiting Reconciliation" value={fmtCurrency(fin.awaiting_reconciliation)} icon={Gauge} tone="gold" onClick={() => openDrill("reconcile")} />
        <Kpi label="Vendor Payout Pending" value={fmtCurrency(fin.vendor_payout_pending)} icon={Building2} tone="slate" onClick={() => openDrill("payout")} />
      </div>

      {/* Lead control */}
      <Section title="Lead Control" icon={Gauge}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Submitted" value={lc.submitted} />
          <Stat label="Awaiting Review" value={lc.awaiting_review} warn={lc.awaiting_review > 0} onClick={() => openDrill("awaiting_review")} />
          <Stat label="Accepted" value={lc.accepted} />
          <Stat label="Accept Rate" value={`${lc.accept_rate || 0}%`} />
          <Stat label="Avg Response" value={`${lc.avg_response_min || 0}m`} />
          <Stat label="Rejected / Returned" value={`${lc.rejected || 0}/${lc.returned || 0}`} />
          <Stat label="Duplicate Flags" value={lc.duplicate_flags} warn={lc.duplicate_flags > 0} onClick={() => openDrill("flags")} />
          <Stat label="DNC Flags" value={lc.dnc_flags} warn={lc.dnc_flags > 0} onClick={() => openDrill("flags")} />
          <Stat label="Unassigned" value={lc.unassigned} warn={lc.unassigned > 0} onClick={() => openDrill("no_next_action")} />
          <Stat label="No Next Action" value={lc.no_next_action} warn={lc.no_next_action > 0} onClick={() => openDrill("no_next_action")} />
          <Stat label="Stalled Leads" value={lc.stalled} warn={lc.stalled > 0} onClick={() => openDrill("stalled")} />
        </div>
      </Section>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Vendor Performance" icon={Building2}>
          {(d.vendors || []).length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.vendors} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={44} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="volume" name="Leads" fill={SLATE} radius={[3, 3, 0, 0]} />
                <Bar dataKey="accepted" name="Accepted" fill={GOLD} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </Section>

        <Section title="Policy Flow" icon={ShieldAlert}>
          {policyFlowData.some((x) => x.value) ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={policyFlowData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82} paddingAngle={2}>
                  {policyFlowData.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </Section>
      </div>

      {/* Agent accountability table */}
      <Section title="Agent Accountability" icon={Users}>
        <div className="mb-3 flex flex-wrap gap-2">
          {[["calls_due", "Calls Due Today"], ["overdue", "Overdue Follow-Ups"], ["missing_info", "Missing Information"],
            ["apps_incomplete", "Applications Incomplete"], ["underwriting", "Underwriting Cases"], ["integration", "Credential / Integration Exceptions"]].map(([m, label]) => (
            <button key={m} onClick={() => openDrill(m)} data-testid={`drill-chip-${m}`}
              className="rounded-full bg-secondary px-3 py-1 text-[11px] font-medium text-foreground/80 ring-1 ring-inset ring-border hover:bg-[#17324D] hover:text-white transition-colors">
              {label} →
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agent-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                {["Agent", "Workload", "New", "Calls Due", "Overdue", "Apps", "Submitted", "UW", "Issued", "Paid", "Conv.", "Missing Disp."].map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(d.agents || []).map((a) => (
                <tr key={a.name} className="border-b border-border/60 cursor-pointer hover:bg-secondary/50" onClick={() => openDrill("agent", a.name)} data-testid={`agent-row-${a.name}`}>
                  <td className="py-2 pr-3 font-medium whitespace-nowrap text-[#315A7D] underline-offset-2 hover:underline">{a.name}</td>
                  <td className="py-2 pr-3">{a.workload}</td>
                  <td className="py-2 pr-3">{a.new_accepted}</td>
                  <td className="py-2 pr-3">{a.calls_due}</td>
                  <td className={`py-2 pr-3 ${a.overdue > 0 ? "text-[#c0492f] font-semibold" : ""}`}>{a.overdue}</td>
                  <td className="py-2 pr-3">{a.apps_started}</td>
                  <td className="py-2 pr-3">{a.submitted}</td>
                  <td className="py-2 pr-3">{a.underwriting}</td>
                  <td className="py-2 pr-3 text-emerald-600 font-medium">{a.issued}</td>
                  <td className="py-2 pr-3">{a.paid}</td>
                  <td className="py-2 pr-3">{a.conversion}%</td>
                  <td className={`py-2 pr-3 ${a.missing_disposition > 0 ? "text-[#7a5d27] font-semibold" : ""}`}>{a.missing_disposition}</td>
                </tr>
              ))}
              {(d.agents || []).length === 0 && <tr><td colSpan={12} className="py-6 text-center text-muted-foreground">No agent activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Vendor table + payout */}
      <Section title="Vendor Economics & Payouts" icon={Building2}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                {["Vendor", "Volume", "Accept %", "Issued %", "Expected Comm", "Received", "Payout Pending", "Payout Paid", "Flags"].map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(d.vendors || []).map((v, vi) => (
                <tr key={v.id || `${v.name}-${vi}`} className="border-b border-border/60 cursor-pointer hover:bg-secondary/50" onClick={() => openDrill("vendor", v.name)} data-testid={`vendor-row-${v.name}`}>
                  <td className="py-2 pr-3 font-medium whitespace-nowrap text-[#315A7D] underline-offset-2 hover:underline">{v.name}</td>
                  <td className="py-2 pr-3">{v.volume}</td>
                  <td className="py-2 pr-3">{v.accept_rate}%</td>
                  <td className="py-2 pr-3">{v.issued_rate}%</td>
                  <td className="py-2 pr-3">{fmtCurrency(v.expected_comm)}</td>
                  <td className="py-2 pr-3 text-emerald-600">{fmtCurrency(v.received_comm)}</td>
                  <td className="py-2 pr-3">{fmtCurrency(v.payout_pending)}</td>
                  <td className="py-2 pr-3">{fmtCurrency(v.payout_paid)}</td>
                  <td className={`py-2 pr-3 ${v.flags > 0 ? "text-[#c0492f] font-semibold" : "text-muted-foreground"}`}>{v.flags}</td>
                </tr>
              ))}
              {(d.vendors || []).length === 0 && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No vendors configured yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Risk & control */}
      <Section title="Risk & Control" icon={ShieldAlert}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="DNC / Opt-Out Incidents" value={risk.dnc_incidents} warn={risk.dnc_incidents > 0} />
          <Stat label="Blocked Calls" value={risk.blocked_calls} />
          <Stat label="Vault Access Events" value={risk.vault_events} />
          <Stat label="Unverified Integrations" value={risk.unverified_integrations} warn={risk.unverified_integrations > 0} />
          <Stat label="Elevated-Permission Users" value={risk.elevated_users} />
          <Stat label="Audit Events" value={risk.audit_events} />
        </div>
        {(risk.integration_readiness || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {risk.integration_readiness.map((ig) => (
              <span key={ig.name} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium ring-1 ring-inset ${
                ig.status === "connected" ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : ig.status === "pending_approval" ? "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30"
                : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-current" /> {ig.name} · {ig.status.replace("_", " ")}
              </span>
            ))}
          </div>
        )}
      </Section>

      {drill && <DrillDialog metric={drill.metric} dkey={drill.key} isDemo={isDemo} onClose={() => setDrill(null)} />}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone, onClick }) {
  const tones = { navy: "bg-[#17324D] text-white", emer: "bg-[#0f9d6b] text-white", gold: "bg-[#B28A3D] text-white", slate: "bg-[#315A7D] text-white" };
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={onClick ? `kpi-${label}` : undefined}
      className={`rounded-xl p-4 text-left ${tones[tone]} ${onClick ? "cursor-pointer transition-transform hover:-translate-y-0.5" : "cursor-default"}`}>
      <div className="flex items-center justify-between opacity-80"><span className="text-[11px] uppercase tracking-wide">{label}</span><Icon className="h-4 w-4" /></div>
      <div className="mt-1.5 font-heading text-2xl font-bold">{value}</div>
      {onClick && <div className="mt-0.5 text-[10px] opacity-70">View records →</div>}
    </button>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="overline mb-3 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {title}</div>
      {children}
    </section>
  );
}

function Stat({ label, value, warn, onClick }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={onClick ? `stat-${label}` : undefined}
      className={`rounded-lg border p-3 text-left ${warn ? "border-[#B28A3D]/40 bg-[#B28A3D]/5" : "border-border"} ${onClick ? "cursor-pointer hover:border-[#315A7D] transition-colors" : "cursor-default"}`}>
      <div className={`font-heading text-xl font-bold ${warn ? "text-[#7a5d27]" : ""}`}>{value ?? 0}</div>
      <div className="text-[11px] text-muted-foreground leading-tight">{label}{onClick ? " →" : ""}</div>
    </button>
  );
}

function DrillDialog({ metric, dkey, isDemo, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const params = new URLSearchParams({ metric, mode: isDemo ? "demo" : "live" });
    if (dkey) params.set("key", dkey);
    api.get(`/command-center/drill?${params.toString()}`).then(({ data }) => setData(data)).catch(() => setData({ rows: [], columns: [], chips: [] }));
  }, [metric, dkey, isDemo]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {data?.title || "Loading…"}
            {isDemo && <span className="rounded-full bg-[#B28A3D]/15 px-2 py-0.5 text-[10px] font-semibold text-[#7a5d27]">Sample data</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          {(data?.chips || []).map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ring-border">{c.label}</span>
          ))}
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="drill-clear"><X className="mr-1 h-3.5 w-3.5" /> Clear Filters</Button>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="drill-back"><ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to Command Center</Button>
        </div>
        {!data ? (
          <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : data.rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground" data-testid="drill-empty">
            No records match this filter right now.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/60 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  {data.columns.map((c) => <th key={c} className="px-3 py-2 font-medium capitalize whitespace-nowrap">{c.replace("_", " ")}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-t border-border/60">
                    {data.columns.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{row[c] ?? "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">{data?.rows?.length || 0} record(s) · role-scoped in Live Mode.</p>
      </DialogContent>
    </Dialog>
  );
}

const Empty = () => <div className="grid h-[240px] place-items-center text-sm text-muted-foreground">No data in live mode yet — switch to Presentation Mode for a full walkthrough.</div>;
